import type { LandingTheme } from '@/pages/Landing/composables/useLandingTheme';

export type ThemedLandingAsset = { dark: string; light?: string };

export interface LandingScreenshot {
    id: string;
    image: ThemedLandingAsset;
    title: string;
    description: string;
}

export const LANDING_SCREENSHOT_BASE_PATH = '/images/landing-page';

export const getLandingImageSrc = (filename: string) => `${LANDING_SCREENSHOT_BASE_PATH}/${filename}`;

export const getThemedLandingImageSrc = (image: ThemedLandingAsset, theme: LandingTheme) => {
    return getLandingImageSrc(theme === 'light' ? (image.light ?? image.dark) : image.dark);
};

export const landingHeroImage: ThemedLandingAsset = {
    dark: 'analytics.png',
    light: 'analytics_light.png',
};

export const landingScreenshots: LandingScreenshot[] = [
    {
        id: 'analytics-overview',
        image: { dark: 'analytics-overview.png', light: 'analytics-overview_light.png' },
        title: 'Analytics Overview',
        description: 'AI-powered business summary with revenue and profit metrics',
    },
    {
        id: 'analytics-charts',
        image: { dark: 'analytics-charts.png', light: 'analytics-charts_light.png' },
        title: 'Performance Charts',
        description: 'Visualize revenue vs profit trends over time',
    },
    {
        id: 'analytics-inventory',
        image: { dark: 'analytics-inventory.png', light: 'analytics-inventory_light.png' },
        title: 'Inventory Analytics',
        description: 'Track ingredient usage and spending patterns',
    },
    {
        id: 'orders-table',
        image: { dark: 'orders-table.png', light: 'orders-table_light.png' },
        title: 'Orders Management',
        description: 'View pending orders with stock status warnings',
    },
    {
        id: 'order-details',
        image: { dark: 'order-details.png', light: 'order-details_light.png' },
        title: 'Order Details',
        description: 'Complete order breakdown with customer info and items',
    },
    {
        id: 'scheduled-order',
        image: { dark: 'scheduled-order.png', light: 'scheduled-order_light.png' },
        title: 'Scheduled Orders',
        description: 'Set up recurring orders with automated invoicing',
    },
    {
        id: 'point-of-sale',
        image: { dark: 'point-of-sale.png', light: 'point-of-sale_light.png' },
        title: 'Point of Sale',
        description: 'Process live sales with cart and session tracking',
    },
    {
        id: 'send-invoice',
        image: { dark: 'send-invoice.png', light: 'send-invoice_light.png' },
        title: 'Send Invoice',
        description: 'Email professional invoices directly to customers',
    },
    {
        id: 'ingredients',
        image: { dark: 'ingredients.png', light: 'ingredients_light.png' },
        title: 'Ingredients Management',
        description: 'Track supplies with costs and quantities',
    },
    {
        id: 'inventory-movements',
        image: { dark: 'inventory-movements.png', light: 'inventory-movements_light.png' },
        title: 'Inventory Movements',
        description: 'Full audit trail of stock changes',
    },
    {
        id: 'recipe-cards',
        image: { dark: 'recipe-cards.png', light: 'recipe-cards_light.png' },
        title: 'Recipe Cards',
        description: 'Beautiful recipe cards with cost and timing details',
    },
    {
        id: 'recipe-walkthrough',
        image: { dark: 'recipe-walkthrough.png', light: 'recipe-walkthrough_light.png' },
        title: 'Recipe Walkthrough',
        description: 'Step-by-step guide with highlighted ingredients',
    },
    {
        id: 'make-product',
        image: { dark: 'make-product.png', light: 'make-product_light.png' },
        title: 'Production Planning',
        description: 'Make products with ingredient requirements and shortage alerts',
    },
    {
        id: 'customers',
        image: { dark: 'customers.png', light: 'customers_light.png' },
        title: 'Customer Management',
        description: 'Organize contacts with preferences and details',
    },
    {
        id: 'calendar',
        image: { dark: 'calendar.png', light: 'calendar_light.png' },
        title: 'Calendar View',
        description: 'See orders and events together at a glance',
    },
    {
        id: 'settings-org',
        image: { dark: 'settings-org.png', light: 'settings-org_light.png' },
        title: 'Organization Settings',
        description: 'Configure business details and order preferences',
    },
    {
        id: 'settings-profile',
        image: { dark: 'settings-profile.png', light: 'settings-profile_light.png' },
        title: 'Profile & Notifications',
        description: 'Personalize appearance and email alerts',
    },
    {
        id: 'recurring-expenses',
        image: { dark: 'recurring-expenses.png', light: 'recurring-expenses_light.png' },
        title: 'Recurring Expenses',
        description: 'Automate regular business expenses',
    },
];
